import moment from 'moment';

import { CoverageDto, EoiContextsDto, PluginEoiDto } from 'theSource/queries';
import { SubscriberDocumentsResponseDto } from '../types';
import { ROUTES } from 'theSource/app/routes';

interface DocumentStatus {
  id: number;
  status: string;
}

interface Document {
  id: number;
  status: string;
  extended_status: string;
  dependent_iq: boolean;
  number_of_documents_required: number;
  number_of_secondary_documents_required: number;
  submitted_documents: DocumentStatus[];
}

export function useToDoListItems(
  subscriberDocumentsState: SubscriberDocumentsResponseDto,
  pluginEoiData: EoiContextsDto,
  coveragesPendingEoi: { current: CoverageDto[]; next: CoverageDto[] },
  t: any
) {
  const toDoList = [];

  const subscriberDocItem = getSubscriberDocuments(subscriberDocumentsState, t);
  if (subscriberDocItem) {
    toDoList.push(subscriberDocItem);
  }

  const pluginEoiPendedTaskItem = getPluginEoiPendedTask(pluginEoiData, t);
  if (pluginEoiPendedTaskItem) {
    toDoList.push(pluginEoiPendedTaskItem);
  }

  const flattenedCoveragesPendingEoi = [
    ...coveragesPendingEoi.current,
    ...coveragesPendingEoi.next,
  ];

  if (flattenedCoveragesPendingEoi.length > 0 && !pluginEoiPendedTaskItem) {
    const isWaitingForEoi = flattenedCoveragesPendingEoi.some((coverage) => {
      return coverage.waiting_for_eoi;
    });
    const task = {
      name: t('answerEOIquestions'),
      path: ROUTES.toDoList.eoi.path,
      value: '1',
      secondaryText: '',
      completed: !isWaitingForEoi,
    };
    toDoList.push(task);
  }

  return toDoList;
}

export const getHasPluginEoi = (eoiData: EoiContextsDto) => {
  let hasEoi = false;
  
  if (eoiData && typeof eoiData === 'object') {
    hasEoi = Object.keys(eoiData).some((key) => {
      const eoiKey = key as keyof EoiContextsDto;
      if (eoiData[eoiKey]) {
        return Object.keys(eoiData[eoiKey]).some((key2) => {
          const eoiItemBase = eoiData[eoiKey][key2];
          if (eoiItemBase.waiting_for_eoi) {
            return true;
          }
          return Object.keys(eoiData[eoiKey][key2]).some((key3) => {
            const eoiItem = eoiData[eoiKey][key2][key3];
            if (
              typeof eoiItem.waiting_for_eoi !== 'object' &&
              'waiting_for_eoi' in eoiItem
            ) {
              return eoiData[eoiKey][key2][key3].waiting_for_eoi;
            } else {
              return Object.keys(eoiData[eoiKey][key2][key3]).some((key4) => {
                const eoiKey4 = key4 as keyof PluginEoiDto;
                const eoiItem2 = eoiData[eoiKey][key2][key3][eoiKey4];
                if (eoiItem2) {
                  return (
                    typeof eoiItem2 === 'object' &&
                    'waiting_for_eoi' in eoiItem2 &&
                    eoiItem2.waiting_for_eoi
                  );
                }
                return false;
              });
            }
          });
        });
      }
      return false;
    });
    return hasEoi;
  } else {
    return false;
  }
};

const getSubscriberDocuments = (
  subscriberDocumentsState: SubscriberDocumentsResponseDto,
  t: any
) => {
  const dueDate = subscriberDocumentsState?.latest_pending_date
    ? moment(subscriberDocumentsState?.latest_pending_date)
        .format('MMM DD YYYY')
        .toUpperCase()
    : '';
  const isRequestedDocPending =
    subscriberDocumentsState?.pending_requests.length > 0;
  const subscriberDocuments =
    subscriberDocumentsState?.document_requests?.subscriber;
  const dependentDocuments =
    subscriberDocumentsState?.document_requests?.dependents;
  const dueDateText =
    isRequestedDocPending && dueDate ? t('dueText', { due_date: dueDate }) : '';
  const anyDocumentRequested = isAnyDocumentRequested(
    subscriberDocuments,
    dependentDocuments
  );
  const anyDocumentReissued = isAnyDocumentReissued(
    subscriberDocuments,
    dependentDocuments
  );

  const taskCompleted = !anyDocumentRequested && !anyDocumentReissued;

  if (
    (subscriberDocuments && subscriberDocuments.length > 0) ||
    (dependentDocuments && hasDepenendentDocRequests(dependentDocuments))
  ) {
    return {
      name: t('uploadRequiredDocument'),
      path: ROUTES.toDoList.addYourDocuments.path,
      value: '0',
      secondaryText: dueDateText,
      completed: taskCompleted,
    };
  }
};

const getPluginEoiPendedTask = (eoiData: EoiContextsDto, t: any) => {
  const isWaitingForEoi = getHasPluginEoi(eoiData);

  if (!isWaitingForEoi) {
    return undefined;
  }
  const task = {
    name: t('answerEOIquestions'),
    path: ROUTES.toDoList.eoi.path,
    value: '1',
    secondaryText: '',
    completed: !isWaitingForEoi,
  };
  return task;
};

export const hasDepenendentDocRequests = (dependentDocuments: any) => {
  let hasDocs = false;
  if (dependentDocuments) {
    Object.keys(dependentDocuments).forEach((key) => {
      const docs = dependentDocuments[key];
      if (docs && docs.length > 0) {
        hasDocs = true;
      }
    });
  }
  return hasDocs;
};

const isAnyDocumentRequested = (
  subscriberDocuments: any,
  dependentDocuments: any
) => {
  return (
    (subscriberDocuments &&
      subscriberDocuments.some(
        (doc: Document) => doc.status === 'requested'
      )) ||
    (dependentDocuments &&
      Object.values(dependentDocuments).some((docs: unknown) =>
        (docs as Document[]).some((doc: Document) => doc.status === 'requested')
      ))
  );
};

const isAnyDocumentReissued = (
  subscriberDocuments: any,
  dependentDocuments: any
) => {
  return (
    (subscriberDocuments &&
      subscriberDocuments.some(
        (doc: Document) =>
          doc.status === 'rejected' && doc.extended_status === 'reissued'
      )) ||
    (dependentDocuments &&
      Object.values(dependentDocuments).some((docs: unknown) =>
        (docs as Document[]).some((doc: Document) => {
          if (doc.dependent_iq && doc.status === 'submitted') {
            return (
              doc.submitted_documents.filter(
                (doc: DocumentStatus) =>
                  !(
                    doc.status === 'rejected_reissued' ||
                    doc.status === 'removed_reissued'
                  )
              ).length <
              doc.number_of_documents_required +
                doc.number_of_secondary_documents_required
            );
          } else {
            return (
              doc.status === 'rejected' && doc.extended_status === 'reissued'
            );
          }
        })
      ))
  );
};
